import React, { useCallback, useContext, useMemo } from 'react';

import DataTable from 'components/DataTable';
import DataTableRenderers from 'components/DataTable/DataTable.renderers';
import Flash from 'components/Flash/Flash';
import buildGridOptions from 'components/Grid/buildGridOptions';
import IncompatibleComparison from 'components/IncompatibleComparison';
import { CardSkeleton } from 'components/Skeleton';
import { RowHeightContext } from 'contextProviders/SplashScreenProviders/MiscProviders/RowHeightProvider';
import MetricMatrixContext from 'contexts/MetricMatrixContext';
import SingleMetricMatrixContext from 'contexts/SingleMetricMatrixContext';
import { useRowHighlightPredicate } from 'hooks/useRowHighlighter';
import Typography from 'kingpin/atoms/Typography';

import ShowAll from './ShowAll';
import useSingleMetricDateMatrixContent from './useSingleMetricDateMatrixContent';

// The Single Metric Date Matrix drawn by DataTable (dataTable gate).
// Same colDefs (hidden comparison columns included), rows, totals and
// renderers as the AG view. Like the AG view, it stays up while more rows
// load ("Load all").
const SingleMetricDateMatrixDataTableView = ({
  gadget,
  isCompatibleComparison,
  isInitialLoading,
  isLoading,
  colDefs,
  rowData,
  totalsRowData,
  visId,
  onDrillDown,
  onRowsSorted,
  isFlashVisible,
  onFlashDismissed,
  currentItems,
  autoSizeFirstColumnOnly,
  singleMetricMatrixContextValue,
  setExportHandle,
}: ReturnType<typeof useSingleMetricDateMatrixContent> & {
  gadget: VisualisationDefinitions.SingleMetricDateMatrix;
  setExportHandle?: (handle: DataTable.ExportHandle | undefined) => void;
}) => {
  const { rowHeight } = useContext(RowHeightContext);
  const isRowHighlighted = useRowHighlightPredicate({
    drillField: gadget.groupByField,
  });
  const isRowSelected = useCallback(
    (data: DataTable.Row) => isRowHighlighted(data) === true,
    [isRowHighlighted],
  );
  const defaultColDef = useMemo(
    () =>
      buildGridOptions({ rowModelType: 'clientSide', rowHeight }).defaultColDef,
    [rowHeight],
  );
  const context = useMemo(() => ({ onDrillDown }), [onDrillDown]);
  const rows = rowData ?? [];
  // The AG view counts the rows it displays after the sort; every loaded
  // row is displayed
  const onSortChanged = useCallback(
    () => onRowsSorted(rows.length),
    [onRowsSorted, rows.length],
  );

  // One value per onDrillDown: a new object each render would re-render
  // every cell that reads it (as the matrix's card resizes)
  const metricMatrixContextValue = useMemo(
    () => ({ onDrillDown }),
    [onDrillDown],
  );

  if (!isCompatibleComparison) {
    return <IncompatibleComparison />;
  }

  if (isInitialLoading) {
    return <CardSkeleton />;
  }

  return (
    <MetricMatrixContext.Provider value={metricMatrixContextValue}>
      <SingleMetricMatrixContext.Provider
        value={singleMetricMatrixContextValue}
      >
        <ShowAll />
        <div style={{ position: 'absolute', width: '100%' }}>
          <Flash
            type="info"
            isVisible={isFlashVisible}
            onDismissed={onFlashDismissed}
            messageHtml={
              <div>
                <Typography.Body
                  type="Body 12"
                  color={'white'}
                >{`Only sorting the ${currentItems} currently displayed`}</Typography.Body>
                <br />
                <Typography.Body type="Body 12">
                  {"Click 'Load All' and then sort to sort all items"}
                </Typography.Body>
              </div>
            }
          />
        </div>
        <DataTable
          tableId={visId}
          testId="grid"
          columnDefs={colDefs}
          defaultColDef={defaultColDef}
          components={DataTableRenderers}
          rows={rows}
          totalsRows={totalsRowData}
          context={context}
          // Sized to fit: flex columns, the first sized by its content.
          // Otherwise every column as wide as its cells, as the AG view's
          // autoSizeAllColumns(skipHeader)
          sizing={gadget.isSizedToFitOverridden ? 'fitWidth' : 'fitContent'}
          isFirstColumnFitted={autoSizeFirstColumnOnly}
          skipHeaderWhenSizing
          onSortChanged={onSortChanged}
          isRowSelected={isRowSelected}
          isLoading={isLoading}
          isHeadlessHeightEnabled
          onExportHandle={setExportHandle}
        />
      </SingleMetricMatrixContext.Provider>
    </MetricMatrixContext.Provider>
  );
};

export default SingleMetricDateMatrixDataTableView;
